/**
 * Runtime configuration, validated once at startup (P0-WEB-04).
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, so a missing variable is
 * not an error — it is the string `undefined` appearing inside a URL. The
 * failure that produces is a request to `undefined/v1/auth/login`, which looks
 * like a routing bug and is nothing of the sort. Reading the environment
 * through one validated accessor turns that into a message that names the
 * variable.
 *
 * Deliberately *not* validated at module import: `pnpm build` runs in CI with
 * no `.env` present, and tests import the client without one. `config()` is
 * called from `main.tsx` before React renders, which is early enough to fail
 * fast and late enough not to break a build that never runs the code.
 */

export interface PortalConfig {
  /** Platform API origin, no trailing slash. */
  readonly apiUrl: string;
  readonly reverb: ReverbConfig;
  /**
   * The MapLibre style the project globe renders (P1-WEB-01). A whole-world
   * vector style; the globe never zooms past country level.
   */
  readonly basemapUrl: string;
  /**
   * The raster tile template the workspace map renders (P1-WEB-05).
   *
   * Separate from the globe's style because the two need different things: the
   * globe wants coastlines at zoom 1, the workspace wants streets at zoom 13,
   * and no free source is good at both. P1-GEO-06 replaces this one.
   */
  readonly basemapTileUrl: string;
  /**
   * The cadastre the draw tools snap to (P1-WEB-07, §AOI-4).
   *
   * A Martin tile template once P1-GEO-07 lands, or a GeoJSON document for
   * development; `cadastre.ts` tells them apart by the `{z}` placeholder. Null
   * when neither has been configured, which turns §AOI-4's toggle off and says
   * why rather than offering a switch that does nothing.
   */
  readonly cadastreUrl: string | null;
  /**
   * A satellite raster tile template, and the credit its licence requires
   * (P1-WEB-28). Null when unset, which leaves "Satellite" out of the basemap
   * switcher rather than offering an entry that renders streets — the
   * production provider is PRD §19 Q1's open question.
   */
  readonly satellite: { readonly tileUrl: string; readonly attribution: string } | null;
}

export interface ReverbConfig {
  /**
   * `REVERB_APP_KEY`. Public by design — it is the Pusher-protocol app key,
   * which identifies the application and authorises nothing. Every private
   * channel is authorised server-side against the session cookie
   * (`routes/channels.php`), so a key in the bundle grants no access.
   */
  readonly key: string;
  /** Host only, no scheme. */
  readonly host: string;
  readonly port: number;
}

export class ConfigError extends Error {
  constructor(readonly missing: readonly string[]) {
    super(
      `Missing required environment ${missing.length === 1 ? 'variable' : 'variables'}: ` +
        `${missing.join(', ')}. Copy apps/portal/.env.example to apps/portal/.env.`,
    );
    this.name = 'ConfigError';
  }
}

type ReadableEnv = Pick<
  ImportMetaEnv,
  | 'VITE_API_URL'
  | 'VITE_REVERB_APP_KEY'
  | 'VITE_REVERB_HOST'
  | 'VITE_REVERB_PORT'
  | 'VITE_BASEMAP_URL'
  | 'VITE_BASEMAP_TILE_URL'
  | 'VITE_CADASTRE_URL'
  | 'VITE_SATELLITE_TILE_URL'
  | 'VITE_SATELLITE_ATTRIBUTION'
>;

/**
 * MapLibre's public demo style.
 *
 * A default rather than a requirement, because the globe is a nice-to-have on
 * a screen whose cards work without it — failing startup over a basemap would
 * trade a degraded globe for no application. P1-GEO-06 points this at our own
 * tiles, at which point it becomes required like the rest.
 */
const DEFAULT_BASEMAP = 'https://demotiles.maplibre.org/style.json';

/**
 * OpenStreetMap's own tiles.
 *
 * A development default. Their usage policy is explicit that heavy embedding is
 * not acceptable, so this must be replaced before anything ships — by P1-GEO-06
 * serving our own, or by a commercial provider once PRD §19 Q1 is answered.
 */
const DEFAULT_BASEMAP_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/**
 * Only the variables something actually reads are required.
 *
 * `VITE_TILES_URL` is declared in `.env.example` but nothing consumes it yet;
 * requiring it would stop a developer who has only configured the API, for no
 * benefit. P1-WEB-05 adds it when it starts reading it.
 *
 * The Reverb variables *are* required, even though realtime is an accelerator
 * rather than the source of truth. `config/broadcasting.php` makes the argument
 * from the other end: a misconfigured broadcaster "presents as a UI where
 * nothing ever updates and no error is logged anywhere". Failing at startup with
 * the variable's name is the only version of that anybody notices.
 */
export function readConfig(env: ReadableEnv): PortalConfig {
  const missing: string[] = [];

  const apiUrl = trimTrailingSlash(env.VITE_API_URL);
  if (apiUrl === undefined) missing.push('VITE_API_URL');

  const key = trimmed(env.VITE_REVERB_APP_KEY);
  if (key === undefined) missing.push('VITE_REVERB_APP_KEY');

  const host = trimmed(env.VITE_REVERB_HOST);
  if (host === undefined) missing.push('VITE_REVERB_HOST');

  const port = Number(trimmed(env.VITE_REVERB_PORT));
  // A non-numeric port is as broken as an absent one, and reports better here
  // than as a socket that silently never opens.
  if (!Number.isInteger(port) || port <= 0) missing.push('VITE_REVERB_PORT');

  if (missing.length > 0) throw new ConfigError(missing);

  // Safe: `missing` is empty, so every value above was present and valid.
  return {
    apiUrl: apiUrl as string,
    reverb: { key: key as string, host: host as string, port },
    basemapUrl: trimmed(env.VITE_BASEMAP_URL) ?? DEFAULT_BASEMAP,
    basemapTileUrl: trimmed(env.VITE_BASEMAP_TILE_URL) ?? DEFAULT_BASEMAP_TILES,
    // No default. There is no public cadastre for Negeri Sembilan to fall back
    // to, and a wrong one would snap boundaries to the wrong lots — which is
    // the failure §AOI-4 exists to prevent, arrived at from the other side.
    cadastreUrl: trimmed(env.VITE_CADASTRE_URL) ?? null,
    satellite: satelliteFrom(env),
  };
}

let cached: PortalConfig | undefined;

/** The validated configuration. Throws `ConfigError` on the first call if the
 *  environment is incomplete. */
export function config(): PortalConfig {
  return (cached ??= readConfig(import.meta.env));
}

/** Tests only — `config()` memoises, and a test that changes the environment
 *  would otherwise see the previous run's values. */
export function resetConfig(): void {
  cached = undefined;
}

/**
 * `http://localhost:8000/` and `http://localhost:8000` must behave alike:
 * the API client concatenates paths that already start with `/`, and the
 * doubled slash produces a 404 on some proxies and works on others.
 */
function trimTrailingSlash(value: string | undefined): string | undefined {
  const cleaned = trimmed(value);
  if (cleaned === undefined) return undefined;
  return cleaned.endsWith('/') ? cleaned.slice(0, -1) : cleaned;
}

/** Whitespace-only is absent, not a value — `VITE_X= ` is a variable somebody
 *  meant to fill in. */
function trimmed(value: string | undefined): string | undefined {
  const cleaned = value?.trim();
  return cleaned === undefined || cleaned === '' ? undefined : cleaned;
}

function satelliteFrom(env: ReadableEnv): PortalConfig['satellite'] {
  const tileUrl = trimmed(env.VITE_SATELLITE_TILE_URL);
  if (tileUrl === undefined) return null;
  return { tileUrl, attribution: trimmed(env.VITE_SATELLITE_ATTRIBUTION) ?? '' };
}
